/**
 * StarRating Component
 * Interactive 5-star rating with half-star precision
 * Used for rating watched content - feeds into Gemini recommendations
 */

import { useState } from 'react';

interface StarRatingProps {
  rating: number; // 0-5, supports half stars (e.g., 3.5)
  onChange?: (rating: number) => void;
  size?: 'sm' | 'md' | 'lg';
  readonly?: boolean;
  showValue?: boolean;
}

export default function StarRating({
  rating,
  onChange,
  size = 'md',
  readonly = false,
  showValue = false,
}: StarRatingProps) {
  const [hoverRating, setHoverRating] = useState<number | null>(null);

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const displayRating = hoverRating ?? rating;

  const handleClick = (starIndex: number, isHalf: boolean) => {
    if (readonly || !onChange) return;
    const newRating = isHalf ? starIndex + 0.5 : starIndex + 1;
    onChange(newRating);
  };

  const handleMouseMove = (e: React.MouseEvent, starIndex: number) => {
    if (readonly) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const isHalf = x < rect.width / 2;
    setHoverRating(isHalf ? starIndex + 0.5 : starIndex + 1);
  };

  return (
    <div className="flex items-center gap-1">
      <div className="flex" onMouseLeave={() => setHoverRating(null)}>
        {[0, 1, 2, 3, 4].map((starIndex) => {
          const fillPercent = Math.min(Math.max(displayRating - starIndex, 0), 1) * 100;

          return (
            <button
              key={starIndex}
              type="button"
              disabled={readonly}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const isHalf = x < rect.width / 2;
                handleClick(starIndex, isHalf);
              }}
              onMouseMove={(e) => handleMouseMove(e, starIndex)}
              className={`relative ${readonly ? 'cursor-default' : 'cursor-pointer'} transition-transform ${
                !readonly ? 'hover:scale-110' : ''
              }`}
            >
              {/* Empty star (background) */}
              <svg
                className={`${sizeClasses[size]} text-gray-600`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>

              {/* Filled star (overlay with clip) */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${fillPercent}%` }}
              >
                <svg
                  className={`${sizeClasses[size]} text-yellow-400`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
            </button>
          );
        })}
      </div>

      {showValue && rating > 0 && (
        <span className="text-sm text-gray-400 ml-1">
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
}

// Quick rating labels for Gemini context
export function getRatingLabel(rating: number): string {
  if (rating >= 4.5) return 'loved';
  if (rating >= 4) return 'really liked';
  if (rating >= 3) return 'liked';
  if (rating >= 2) return 'okay with';
  if (rating >= 1) return 'disliked';
  return 'not rated';
}
